import {
  BarcodeOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  SearchOutlined,
  ShoppingOutlined,
  TagOutlined,
  TeamOutlined,
  UserOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Descriptions,
  Input,
  Popover,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReverseIcon from "../../components/icons/ReverseIcon";
import { IExportSearch, IFilterWarehouse } from "../../models/Warehouse";
import {
  useReverseExportWarehouse,
  useWarehouseExports,
} from "../../query/WarehouseQuery";
import { notify } from "../../utils/toast";

const WarehouseListExport: React.FC = () => {
  const [selectedExport, setSelectedExport] = useState<IExportSearch | null>(
    null,
  );

  const [params] = useSearchParams();
  const exportId = params.get("exportId");

  const [searchType, setSearchType] = useState<
    "exportName" | "exporter" | "partNumber" | "type"
  >("exportName");
  const [filter, setFilter] = useState<IFilterWarehouse>({
    page: 1,
    limit: 10,
  });
  const [reason, setReason] = useState<string>("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setFilter((prev) => ({ ...prev, page: 1, [searchType]: value }));
    }, 300),
    [setFilter, searchType],
  );

  const { data: exportList, isLoading } = useWarehouseExports(filter);
  const reverseExport = useReverseExportWarehouse();
  const handleReverse = async (id: number, reason: string) => {
    try {
      if (!reason.trim()) {
        notify.error("Vui lòng nhập lý do hoàn tác");
        return;
      }
      const response = await reverseExport.mutateAsync({ id, reason });
      notify.success(response.message);
    } catch (error: any) {
      notify.error(error?.message);
    }
  };
  const exportListData = exportList?.data ?? [];
  useEffect(() => {
    if (exportId) {
      setFilter((prev) => ({ ...prev, exportId: Number(exportId) }));
    }
  }, [exportId]);

  const detailColumns = [
    {
      title: "Mã hàng",
      dataIndex: ["partNumber", "partNumber"],
      key: "partNumber",
    },
    {
      title: "Số lượng vận chuyển",
      dataIndex: "deliveryQuantity",
      key: "deliveryQuantity",
    },
    {
      title: "Số lượng xuất",
      dataIndex: "actualQuantity",
      key: "actualQuantity",
    },
  ];

  return (
    <div>
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="w-full lg:w-[350px] xl:w-[400px]">
          <Card size="small" className="mb-4">
            <div className="space-y-3">
              <Select
                defaultValue="exportName"
                className="w-full"
                options={[
                  { label: "Tìm theo tên phiếu", value: "exportName" },
                  { label: "Tìm theo người xuất", value: "exporter" },
                  { label: "Tìm theo mã hàng", value: "partNumber" },
                  { label: "Tìm theo loại phiếu", value: "type" },
                ]}
                onChange={(
                  value: "exportName" | "exporter" | "partNumber" | "type",
                ) => {
                  setFilter({
                    page: 1,
                    limit: 10,
                  });
                  setSearchType(value);
                }}
              />
              {searchType === "type" ? (
                <Select
                  className="w-full"
                  options={[
                    { label: "Phiếu xuất", value: "EXPORT" },
                    { label: "Phiếu hoàn tác", value: "IMPORT" },
                  ]}
                  placeholder="Chọn loại phiếu..."
                  onChange={(value) =>
                    setFilter((prev) => ({ ...prev, page: 1, type: value }))
                  }
                />
              ) : (
                <Input
                  placeholder={
                    searchType === "exportName"
                      ? "Tìm kiếm theo tên phiếu xuất..."
                      : searchType === "exporter"
                        ? "Tìm kiếm theo tên người xuất..."
                        : "Tìm kiếm theo mã hàng..."
                  }
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => debouncedSearch(e.target.value)}
                  className="w-full"
                />
              )}
            </div>
          </Card>
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <Spin indicator={<LoadingOutlined spin />} size="large" />
            </div>
          ) : exportListData.length > 0 ? (
            <div className="space-y-3">
              {exportListData.map((item) => (
                <Card
                  key={item.id}
                  size="small"
                  className={`cursor-pointer hover:shadow-md transition-shadow ${
                    selectedExport?.id === item.id
                      ? "border-blue-500 border-2"
                      : ""
                  }`}
                  onClick={() => setSelectedExport(item)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <FileTextOutlined className="text-blue-500" />
                        <span
                          className={`font-medium text-blue-600 dark:text-blue-300 ${item.type === "IMPORT" && "text-red-500"}`}
                        >
                          {item.name || "(Không có tên)"}
                        </span>
                        <Tag color="blue" className="ml-auto">
                          <BarcodeOutlined /> #{item.id}
                        </Tag>
                      </div>
                      <div className="flex flex-col sm:grid sm:grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                          <UserOutlined className="text-blue-400" />
                          {item.createdBy?.fullName || "Chưa cập nhật"}
                        </div>
                        <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                          <CalendarOutlined className="text-green-400" />
                          {item.createdAt
                            ? new Date(item.createdAt).toLocaleDateString()
                            : "--/--/----"}
                        </div>
                        <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                          <ShoppingOutlined className="text-orange-400" />
                          {(item.partNumberExports?.length || 0) + " mã hàng"}
                        </div>
                        <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                          <TagOutlined className="text-purple-400" />
                          {item.customerName || "Chưa cập nhật"}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}

              <div className="flex justify-center pt-4">
                <Space>
                  <Button
                    disabled={filter.page === 1}
                    onClick={() =>
                      setFilter((prev) => ({
                        ...prev,
                        page: (prev.page || 1) - 1,
                      }))
                    }
                  >
                    Trang trước
                  </Button>
                  <span className="px-4">
                    Trang {filter.page} /{" "}
                    {exportList?.pagination?.totalPages || 1}
                  </span>
                  <Button
                    disabled={
                      filter.page === exportList?.pagination?.totalPages
                    }
                    onClick={() =>
                      setFilter((prev) => ({
                        ...prev,
                        page: (prev.page || 1) + 1,
                      }))
                    }
                  >
                    Trang sau
                  </Button>
                </Space>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-4">
              Không tìm thấy kết quả
            </div>
          )}
        </div>

        <div className="flex-1 max-h-[calc(100vh-100px)] lg:max-h-screen flex flex-col overflow-auto">
          {selectedExport ? (
            <Card
              title={
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <Typography.Title
                    level={4}
                    style={{
                      margin: 0,
                      fontFamily: "Chakra Petch",
                      textTransform: "uppercase",
                    }}
                  >
                    Chi tiết xuất kho
                  </Typography.Title>
                  <Popover
                    content={
                      <div style={{ maxWidth: 250 }}>
                        <p>Bạn có chắc muốn hoàn tác phiếu này?</p>
                        <Input.TextArea
                          rows={4}
                          placeholder="Lý do hoàn tác..."
                          onChange={(e) => setReason(e.target.value)}
                        />

                        <Button
                          type="primary"
                          danger
                          onClick={() =>
                            handleReverse(selectedExport.id, reason)
                          }
                          style={{ marginRight: 8 }}
                        >
                          Xác nhận
                        </Button>
                        <Button>Huỷ</Button>
                      </div>
                    }
                    trigger="click"
                  >
                    <Button
                      icon={<ReverseIcon />}
                      hidden={selectedExport.type == "IMPORT"}
                      disabled={
                        selectedExport.type == "IMPORT" ||
                        selectedExport.isReversed
                      }
                      type="primary"
                      danger
                    >
                      {selectedExport.isReversed ? "Đã hoàn tác" : "Hoàn tác"}
                    </Button>
                  </Popover>
                </div>
              }
              className="h-full"
            >
              <div className="space-y-6">
                <Card
                  size="small"
                  className="bg-gradient-to-br from-blue-50 to-gray-50 border-blue-200 dark:from-gray-800 dark:to-gray-900 dark:border-gray-700"
                >
                  <div className="mb-4 pb-3 border-b border-blue-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 justify-between">
                      <Space>
                        <Badge status="processing" />
                        <span
                          className={`${selectedExport.type === "IMPORT" && "text-red-500"} text-lg font-semibold text-blue-700 dark:text-blue-300`}
                        >
                          {selectedExport.name}
                        </span>
                      </Space>

                      <Space>
                        <Typography.Text>
                          {selectedExport.type === "IMPORT" &&
                            selectedExport.reversedFromId && (
                              <Typography.Text style={{ color: "red" }}>
                                Hoàn tác từ phiếu xuất #
                                {selectedExport.reversedFromId}
                              </Typography.Text>
                            )}
                        </Typography.Text>
                        <Tag color="blue" className="ml-auto">
                          <BarcodeOutlined /> #{selectedExport.id}
                        </Tag>
                      </Space>
                    </div>
                  </div>

                  <Descriptions column={2} size="small">
                    <Descriptions.Item
                      label={
                        <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                          <TagOutlined className="text-blue-500" /> Project Code
                        </div>
                      }
                      span={2}
                    >
                      <span className="font-medium text-blue-600 dark:text-blue-400">
                        {selectedExport.partNumberExports?.[0]?.partNumber
                          ?.projectCode || "-"}
                      </span>
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={
                        <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                          <TeamOutlined className="text-green-500" /> Người nhận
                          hàng
                        </div>
                      }
                    >
                      <span className="font-medium text-blue-600 dark:text-blue-400">
                        {selectedExport.consignee || "Chưa cập nhật"}
                      </span>
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={
                        <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                          <ShoppingOutlined className="text-orange-500" /> Khách
                          hàng
                        </div>
                      }
                    >
                      <span className="font-medium text-blue-600 dark:text-blue-400">
                        {selectedExport.customerName || "Chưa cập nhật"}
                      </span>
                    </Descriptions.Item>
                    {selectedExport.type === "IMPORT" && (
                      <Descriptions.Item
                        label={
                          <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                            <ReverseIcon className="text-orange-500" /> Lý do
                            hoàn tác
                          </div>
                        }
                      >
                        <span className="font-medium text-blue-600 dark:text-blue-400">
                          {selectedExport.reverseReason || "Chưa cập nhật"}
                        </span>
                      </Descriptions.Item>
                    )}

                    <Descriptions.Item
                      label={
                        <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                          <EnvironmentOutlined className="text-red-500" /> Địa
                          chỉ giao hàng
                        </div>
                      }
                    >
                      <span className="text-gray-800 dark:text-gray-200">
                        {selectedExport.deliveryAddress || "-"}
                      </span>
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={
                        <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                          <CalendarOutlined className="text-indigo-500" /> Ngày
                          xuất
                        </div>
                      }
                    >
                      <span className="text-gray-800 dark:text-gray-200">
                        {selectedExport.createdAt
                          ? new Date(selectedExport.createdAt).toLocaleString()
                          : "-"}
                      </span>
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={
                        <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                          <UserOutlined className="text-cyan-500" /> Người xuất
                        </div>
                      }
                    >
                      <span className="text-gray-800 dark:text-gray-200">
                        {selectedExport.createdBy?.fullName || "-"}
                      </span>
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={
                        <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                          <BarcodeOutlined className="text-purple-500" /> Số
                          lượng mã hàng
                        </div>
                      }
                    >
                      <Tag color="purple">
                        {selectedExport.partNumberExports?.length || 0}
                      </Tag>
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={
                        <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                          <UserSwitchOutlined className="text-cyan-500" /> Sale
                          Admin
                        </div>
                      }
                    >
                      <span className="text-gray-800 dark:text-gray-200">
                        {selectedExport.saleAdmin?.fullName || "Chưa cập nhật"}
                      </span>
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={
                        <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                          <InfoCircleOutlined className="text-blue-500" /> Ghi
                          chú
                        </div>
                      }
                      span={2}
                    >
                      <span className="text-gray-800 dark:text-gray-200">
                        {selectedExport.note || "Không có ghi chú"}
                      </span>
                    </Descriptions.Item>
                    {selectedExport.type === "EXPORT" &&
                      selectedExport.isReversed && (
                        <Descriptions.Item
                          label={
                            <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                              <ClockCircleOutlined className="text-blue-500" />{" "}
                              Hoàn tác lúc:
                            </div>
                          }
                          span={2}
                        >
                          <span className="text-gray-800 dark:text-gray-200">
                            {dayjs(selectedExport.reverseAt).format(
                              "YYYY-MM-DD HH:mm:ss",
                            ) || "Không có ghi chú"}
                          </span>
                        </Descriptions.Item>
                      )}
                  </Descriptions>
                </Card>

                <div>
                  <Typography.Title
                    level={5}
                    className="flex items-center gap-2"
                  >
                    <BarcodeOutlined className="text-blue-500" />
                    Danh sách mã hàng
                  </Typography.Title>
                  <div className="overflow-x-auto">
                    <Table
                      columns={detailColumns}
                      dataSource={selectedExport.partNumberExports}
                      pagination={false}
                      size="small"
                      rowKey="id"
                      bordered
                      scroll={{ x: "max-content" }}
                      className="[&_.ant-table-cell]:!border-gray-300"
                    />
                  </div>
                </div>
              </div>
            </Card>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500">
              Chọn một phiếu xuất để xem chi tiết
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WarehouseListExport;
